import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Spin from "../../../components/Spinner/Spiner";
import {
  Card,
  Row,
  Col,
  Typography,
  Descriptions,
  List,
  Button,
  Image,
  Divider,
  notification,
  Modal,
  Input,
  Space,
  Tag,
  Table,
  Tooltip,
  Avatar,
  Grid,
} from "antd";
import {
  UploadOutlined,
  PaperClipOutlined,
  ArrowLeftOutlined,
  CopyOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
  FileDoneOutlined,
  IdcardOutlined,
  UserOutlined,
  PhoneOutlined,
  FileImageOutlined,
  PlusOutlined,
  MailOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useStore } from "../../../root-store-context";
import CorrigoConstants, { ALLOWED_DOC_EXTENSIONS_STRING } from "../../../Constants/CorrigoConstants";
import moment from "moment";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;
const ACCENT = "#215575";

export default function WorkOrderDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { workOrderStore, applicationStore } = useStore();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [loading, setLoading] = useState(false);
  const [wo, setWo] = useState(null);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [noteModalVisible, setNoteModalVisible] = useState(false);
  const [noteBody, setNoteBody] = useState("");
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  const fileInputRef = useRef(null);

  const TAG_STYLE = { borderRadius: 6, background: "#eef3f6", color: ACCENT, border: "1px solid rgba(90,120,144,0.12)" };
  const MAX_UPLOAD_BYTES = CorrigoConstants.MAX_UPLOAD_SIZE_MB * 1024 * 1024;

  const ALLOWED_EXTENSIONS_SET = useMemo(
    () => new Set((CorrigoConstants.ALLOWED_DOC_EXTENSIONS || []).map((e) => String(e).toLowerCase())),
    []
  );

  const stripDataUrlPrefix = (s = "") => {
    const idx = String(s).indexOf(",");
    return idx >= 0 ? s.substring(idx + 1) : s;
  };

  const extractFileName = useCallback((doc = {}) => {
    if (doc.Description) return doc.Description;
    if (doc.Title) return doc.Title;
    if (!doc.DocUrl) return `Document_${doc.Id ?? "?"}`;
    try {
      const parts = String(doc.DocUrl).split("/");
      const last = parts.pop() || parts.pop() || "";
      return decodeURIComponent(last);
    } catch (err) {
      return String(doc.DocUrl).split("/").pop() || `Document_${doc.Id ?? "?"}`;
    }
  }, []);

  const initialsFrom = useCallback((s) => (s ? String(s).split(" ").map((p) => p[0]).slice(0, 2).join("") : "U"), []);

  const validateFile = useCallback(
    (file) => {
      if (!file) return { ok: false, reason: "no-file" };
      const name = file.name || "";
      const dot = name.lastIndexOf(".");
      const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
      if (!ext || !ALLOWED_EXTENSIONS_SET.has(ext)) return { ok: false, reason: "bad-ext" };
      if (file.size > MAX_UPLOAD_BYTES) return { ok: false, reason: "too-large" };
      return { ok: true };
    },
    [ALLOWED_EXTENSIONS_SET]
  );

  const loadWorkOrder = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await workOrderStore.getDetailsForWorkOrder(id, applicationStore?.SelectedAddress?.userId ?? 0);
      const data = res?.data?.Data ?? res?.Data ?? res?.data ?? null;
      setWo(data);
      console.log(data);
    } catch (err) {
      setError("Failed to load details");
    } finally {
      setLoading(false);
    }
  }, [id, workOrderStore]);

  useEffect(() => { loadWorkOrder(); }, [loadWorkOrder]);

  const handleAddClick = useCallback(() => fileInputRef.current?.click(), []);

  const handleFileSelected = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const valid = validateFile(file);
    if (!valid.ok) {
      if (valid.reason === "bad-ext") notification.error({ message: `File "${file.name}" is not a supported format.` });
      else if (valid.reason === "too-large") notification.error({ message: `File "${file.name}" exceeds the ${CorrigoConstants.MAX_UPLOAD_SIZE_MB} MB limit.` });
      else notification.error({ message: `Invalid file: ${file.name}` });
      e.target.value = ""; return;
    }

    setUploading(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const title =
        file.name.length > CorrigoConstants.MAX_TITLE_LENGTH
          ? file.name.substring(0, CorrigoConstants.MAX_TITLE_LENGTH)
          : file.name;
      const payload = {
        entity: {
          encryptedId: String(wo?.id ?? id),
          actorTypeId: "WO",
          description: file.name,
          title: title,
          isShared: false,
          isPublic: true,
          storageTypeId: "Cloud",
          docType: { DisplayAs: "File" },
          blob: { body: stripDataUrlPrefix(dataUrl), fileName: file.name },
        },
      };

      if (workOrderStore?.addAttachment) await workOrderStore.addAttachment(payload);
      else {
        const resp = await fetch("/api/AddAttachment", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
        if (!resp.ok) throw new Error(await resp.text());
      }

      notification.success({ message: "Attachment uploaded", description: file.name });
      await loadWorkOrder();
    } catch (err) {
      notification.error({ message: "Upload failed", description: String(err.message || err) });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }, [id, loadWorkOrder, validateFile, workOrderStore, wo]);

  const openNoteModal = useCallback(() => { setNoteBody(""); setNoteModalVisible(true); }, []);
  const closeNoteModal = useCallback(() => setNoteModalVisible(false), []);

  const handleNoteSubmit = useCallback(async () => {
    if (!noteBody?.trim()) {
      notification.warning({ message: "Note is empty", description: "Please enter a note body." });
      return;
    }
    setNoteSubmitting(true);
    try {
      const payload = { entity: { body: noteBody, encryptedId: String(wo?.id ?? id), noteTypeId: "Public" } };
      await workOrderStore.addNote(payload);
      notification.success({ message: "Note added" });
      await loadWorkOrder();
      closeNoteModal();
    } catch (err) {
      notification.error({ message: "Failed to add note", description: String(err.message || err) });
    } finally {
      setNoteSubmitting(false);
    }
  }, [id, noteBody, loadWorkOrder, workOrderStore, wo, closeNoteModal]);

  const isCancelled = useMemo(() => {
    return wo?.StatusId === CorrigoConstants.CANCELLED_STATUS;
  }, [wo]);

  // derived
  const publicNotes = useMemo(() => {
    const arr = Array.isArray(wo?.Notes) ? wo.Notes.filter((n) => ((n?.NoteTypeId ?? n?.NoteType?.Id ?? "") + "").toLowerCase() === "public") : [];
    return arr.slice().sort((a, b) => (moment.utc(b?.CreatedDate ?? b?.CreatedDateUtc ?? b?.Created).valueOf() || 0) - (moment.utc(a?.CreatedDate ?? a?.CreatedDateUtc ?? a?.Created).valueOf() || 0));
  }, [wo]);

  const items = Array.isArray(wo?.Items) ? wo.Items : [];
  const contactAddrRaw = String(wo?.ContactAddress?.Address ?? "").trim();
  let contactPhone = "", contactEmail = "";
  if (contactAddrRaw) contactAddrRaw.split(",").map(p => p.trim()).filter(Boolean).forEach(p => { if (p.includes("@") && !contactEmail) contactEmail = p; else if (!contactPhone) contactPhone = p; });

  const columns = useMemo(() => [
    { title: "Task", key: "task", render: (_, r) => r?.Task?.DisplayAs ?? r?.Task?.Id ?? `Task ${r?.TaskId ?? ""}` },
    { title: "Asset", key: "asset", render: (_, r) => r?.Asset?.Name ?? r?.AssetLocation ?? r?.Asset?.Id ?? "—" },
    { title: "Asset Location", key: "assetLocation", render: (_, r) => r?.AssetLocation?.Name ?? r?.AssetLocation ?? r?.Asset?.Location ?? "—" },
    { title: "Concern / Description", key: "concern", width: isMobile ? undefined : 420, render: (_, r) => {
        const text = r?.Comment ?? r?.Description ?? "—";
        if (!text || text === "—") return "—";
        return (
          <Typography.Paragraph ellipsis={{ rows: 2, expandable: true, symbol: "more" }} style={{ margin: 0, maxWidth: isMobile ? "100%" : 420, whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.4 }}>{text}</Typography.Paragraph>
        );
      } },
  ], [isMobile]);

  const noteCount = publicNotes.length;
  const attachmentCount = Array.isArray(wo?.Documents) ? wo.Documents.length : 0;
  const itemCount = items.length;

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}><Spin tip="Loading work order..." /></div>;
  if (error) return <div style={{ padding: 24 }}><Typography.Paragraph type="danger">{error}</Typography.Paragraph><Button onClick={() => navigate(-1)}>Back</Button></div>;
  if (!wo) return <div style={{ padding: 24 }}><Typography.Paragraph>No work order found</Typography.Paragraph><Button onClick={() => navigate(-1)}>Back</Button></div>;

  const formatAuthorName = (typeId) => {
    const normalized = String(typeId ?? "").trim();
    if (normalized === CorrigoConstants.USER) return CorrigoConstants.USER_TO_SHOW;
    if (normalized === CorrigoConstants.CORRIGO_ADMIN) return CorrigoConstants.CORRIGO_ADMIN_TO_SHOW;
    return "Unknown";
  };

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: "0 auto" }}>
      <style>{` .ant-table-row:hover { background-color: #f0f4f7 !important; } .ant-table-thead > tr > th { background-color: #00000005 !important; color: ${ACCENT} !important; font-weight: 600; border-bottom: 1px solid #e5e8eb !important; } .ant-table-tbody > tr > td { background-color: #ffffffff !important; color: #000000ff !important; border-bottom: 1px solid #e5e8eb !important; } .ant-table-cell { border-right: 1px solid #f0f0f0; } .ant-table-cell:last-child { border-right: none; } .ant-list-item:hover { background-color: #f6fafc !important; transition: background 0.2s ease-in-out; } `}</style>

      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col>
          <div style={{ display: "flex", alignItems: "baseline", gap: 12 }}>
            <Tooltip title="Go back"><ArrowLeftOutlined style={{ fontSize: 18, cursor: "pointer", color: ACCENT }} onClick={() => navigate(-1)} /></Tooltip>
            <Title level={4} style={{ margin: 0 }}>{wo?.Number ?? wo?.Id}</Title>
            <Tag style={TAG_STYLE}>{wo?.StatusId ?? "—"}</Tag>
          </div>
        </Col>
        <Col>
          <Space size="middle">
            <Tooltip title="Copy link to this work order"><CopyOutlined style={{ fontSize: 18, cursor: "pointer", color: ACCENT }} onClick={() => { navigator.clipboard?.writeText(window.location.href); notification.success({ message: "Link copied" }); }} /></Tooltip>
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: 18, borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", background: "#fafafa" }} bodyStyle={{ padding: isMobile ? 12 : 20 }}>
        <Descriptions title={<Space><IdcardOutlined style={{ color: ACCENT }} /><Text strong style={{ fontSize: 16 }}>Work Order Details</Text></Space>} column={isMobile ? 1 : 2} size="middle" labelStyle={{ fontWeight: 500, width: 140 }} contentStyle={{ color: "#333" }}>
          <Descriptions.Item label="ID"><Tag style={TAG_STYLE}>{wo?.Id ?? "—"}</Tag></Descriptions.Item>
          <Descriptions.Item label="Priority">{wo?.Priority?.DisplayAs ? (<Tag style={TAG_STYLE}>{wo.Priority.DisplayAs}</Tag>) : ("—")}</Descriptions.Item>
          <Descriptions.Item label="Created Date">{wo?.CreatedDateUtc ? moment.utc(wo.CreatedDateUtc).local().format("MMM D, YYYY, hh:mm A") : wo?.DtCreated ?? "—"}</Descriptions.Item>
          <Descriptions.Item label="Due Date">{wo?.DueDateUtc ? moment.utc(wo.DueDateUtc).local().format("MMM D, YYYY, hh:mm A") : wo?.DtDue ?? "—"}</Descriptions.Item>
          <Descriptions.Item label="Assigned To">{wo?.Employee?.DisplayAs ?? "—"}</Descriptions.Item>
          <Descriptions.Item label="Contact Name"><Space><UserOutlined />{wo?.ContactName ?? "—"}</Space></Descriptions.Item>
          <Descriptions.Item label="Contact Phone"><Space><PhoneOutlined />{contactPhone || "—"}</Space></Descriptions.Item>
          <Descriptions.Item label="Contact Email"><Space><MailOutlined />{contactEmail || "—"}</Space></Descriptions.Item>
        </Descriptions>
      </Card>

      <Card style={{ marginBottom: 18, borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", overflow: "hidden" }} bodyStyle={{ padding: 0 }} title={<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <UnorderedListOutlined style={{ color: ACCENT, fontSize: 18 }} />
          <Text strong style={{ fontSize: 16 }}>Asset / Tasks</Text>
          <Tag style={{ background: "#f3f7f9", color: ACCENT, border: "none", fontWeight: 500 }}>{itemCount}</Tag>
        </div>
      </div>}>
        {items.length ? (
          <Table columns={columns} dataSource={items} rowKey={(r, i) => r?.Id ?? r?.TaskId ?? i} pagination={false} size="middle" bordered={false} style={{ borderRadius: 8, overflow: "hidden" }} />
        ) : (
          <div style={{ textAlign: "center", padding: "48px 16px", background: "#fafbfc", borderTop: "1px solid #f0f0f0" }}>
            <FileTextOutlined style={{ fontSize: 36, color: "#b0b8c2" }} />
            <div style={{ marginTop: 12 }}><Text type="secondary">No Asset / Task items added yet.</Text></div>
          </div>
        )}
      </Card>

      <Row gutter={16}>
        <Col span={14}>
          <Card style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", overflow: "hidden" }} title={<div style={{ display: "flex", alignItems: "center", gap: 10 }}><FileTextOutlined style={{ color: ACCENT, fontSize: 18 }} /><Text strong style={{ fontSize: 16, color: "#1f2f3f" }}>Notes</Text><Tag style={{ background: "#f3f7f9", color: ACCENT, border: "none", fontWeight: 500 }}>{noteCount}</Tag></div>} extra={<Tooltip title={isCancelled ? "Note cannot be created for Cancelled Work Order " : ""}><Button type="primary" icon={<PlusOutlined />} onClick={openNoteModal} style={{ background: '#5a7890', borderColor: ACCENT, borderRadius: 6, fontWeight: 500 }} disabled={isCancelled}>Add Note</Button></Tooltip>}>
            {publicNotes.length ? (
              <List dataSource={publicNotes} itemLayout="vertical" split={false} renderItem={(note) => {
                const formatted = moment(note?.CreatedDate ?? note?.CreatedDateUtc ?? note?.Created).isValid() ? moment.utc(note?.CreatedDate ?? note?.CreatedDateUtc ?? note?.Created).local().format("M/D/YYYY") : "";
                const initials = initialsFrom(formatAuthorName(note?.CreatedBy?.TypeId) ?? note?.Author ?? "U");
                return (
                  <List.Item style={{ padding: "8px 0" }}>
                    <div style={{ background: "#00000005", border: "1px solid #edf1f3", borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <Avatar style={{ backgroundColor: "#eaf0f3", color: ACCENT, fontWeight: 600 }}>{initials}</Avatar>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <div><Text strong style={{ color: "#213041" }}>{formatAuthorName(note?.CreatedBy?.TypeId)}</Text></div>
                          <Text type="secondary" style={{ fontSize: 12 }}>{formatted}</Text>
                        </div>
                        <div style={{ marginTop: 8, color: "#3a4a5c", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{note.Body}</div>
                      </div>
                    </div>
                  </List.Item>
                );
              }} />
            ) : (
              <div style={{ textAlign: "center", padding: "48px 16px", background: "#fafbfc", borderTop: "1px solid #f0f0f0" }}>
                <FileTextOutlined style={{ fontSize: 36, color: "#b0b8c2" }} />
                <div style={{ marginTop: 12 }}><Text type="secondary">No notes have been added yet.</Text></div>
                <Button type="link" icon={<PlusOutlined />} style={{ color: ACCENT, marginTop: 8 }} onClick={openNoteModal} disabled={isCancelled}>Add your first note</Button>
              </div>
            )}
          </Card>
        </Col>

        <Col span={10}>
          <Card style={{ borderRadius: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.05)", overflow: "hidden" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <FileDoneOutlined style={{ color: ACCENT }} />
                <Title level={5} style={{ margin: 0 }}>Attachments</Title>
                <Tag style={{ marginLeft: 8, background: "#f3f7f9", color: ACCENT, border: "none" }}>{attachmentCount}</Tag>
              </div>

              <div>
                <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleFileSelected} />
                <Tooltip title={isCancelled ? "Attachments can not be added for Cancelled Work Order" : "Add Attachment"}>
                  <Button disabled={isCancelled} icon={<UploadOutlined />} onClick={handleAddClick} loading={uploading}>Add</Button>
                </Tooltip>
              </div>
            </div>

            <div style={{ marginTop: 15, marginBottom: 40, fontSize: 12, color: ACCENT }}>
              <p><strong>Max upload size:</strong> {CorrigoConstants.MAX_UPLOAD_SIZE_MB} MB <Tooltip title={ALLOWED_DOC_EXTENSIONS_STRING}><span style={{ cursor: "pointer", textDecoration: "underline" }}>(Allowed file formats)</span></Tooltip></p>
            </div>

            <Divider style={{ margin: "8px 0" }} />

            {Array.isArray(wo?.Documents) && wo.Documents.length ? (
              <List grid={{ gutter: 8, column: 1 }} dataSource={wo.Documents} renderItem={(doc) => {
                const fileName = extractFileName(doc);
                const mimeType = String(doc?.MimeType ?? "").toLowerCase();
                const isImage = mimeType.startsWith("image");
                const isUrl = mimeType === "url";
                const href = isUrl && doc?.DocUrl && !doc.DocUrl.startsWith("http") ? `https://${doc.DocUrl}` : doc.DocUrl;

                return (
                  <List.Item key={doc.Id}>
                    <Tooltip title="Click to View">
                      <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: ACCENT, textDecoration: "none" }}>
                        <Card size="small" hoverable>
                          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                            <div style={{ width: 72, height: 56, flex: "0 0 72px" }}>
                              {isImage ? (<Image src={doc.DocUrl} alt={fileName} width={72} height={56} preview={false} />) : isUrl ? (
                                <div style={{ width: 72, height: 56, display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa", borderRadius: 4 }}><LinkOutlined style={{ fontSize: 20, color: ACCENT }} /></div>
                              ) : (
                                <div style={{ width: 72, height: 56, display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa", borderRadius: 4 }}><PaperClipOutlined style={{ fontSize: 20, color: ACCENT }} /></div>
                              )}
                            </div>
                            <div style={{ flex: 1 }}>
                              <p style={{ color: ACCENT, textDecoration: "none", margin: 0, wordBreak: "break-word", whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: "1.4" }}>{fileName}</p>
                              <div style={{ marginTop: 6 }}><Text type="secondary">{isUrl ? "External Link" : doc?.Size ? `${Math.round(doc.Size / 1024)} KB` : ""}</Text></div>
                            </div>
                          </div>
                        </Card>
                      </a>
                    </Tooltip>
                  </List.Item>
                );
              }} />
            ) : (
              <div style={{ textAlign: "center", padding: 32 }}>
                <FileImageOutlined style={{ fontSize: 36, color: "#769ab0ff" }} />
                <div style={{ marginTop: 8 }}><Text type="secondary">No documents</Text></div>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Modal title="Add Note" open={noteModalVisible} onCancel={closeNoteModal} onOk={handleNoteSubmit} confirmLoading={noteSubmitting} okText="Add">
        <Space direction="vertical" style={{ width: "100%" }}>
          <TextArea value={noteBody} onChange={(e) => setNoteBody(e.target.value)} rows={5} placeholder="Write your note here..." />
        </Space>
      </Modal>
    </div>
  );
}
